import { computed, effect, inject, Signal } from '@angular/core';
import { patchState, signalStoreFeature, type, withComputed, withHooks, withState } from '@ngrx/signals';
import { ItemPriceUtil, ItemPricingEntity, ItemPricingStore, stringifyItemId } from 'products-data';

import { CartEntityItem } from '../../types';

export const withCartPricing = () => {
  return signalStoreFeature(
    {
      state: type<{ cartId: string | undefined }>(),
      props: type<{ cartItems: Signal<CartEntityItem[]> }>(),
    },
    withState(() => ({
      _pricing: {} as Record<string, ItemPricingEntity | undefined>,
    })),
    withComputed((store) => {
      const pricingStore = inject(ItemPricingStore);

      const records = computed(() => {
        return store.cartItems().map((i) => {
          const id = stringifyItemId(i.id);
          return {
            id,
            record: pricingStore.getItemPricing(i.id),
          };
        });
      });

      const loading = computed(() => records().some((r) => r.record.loading()));
      const errors = computed(() => {
        const errs: Record<string, unknown> = {};
        let hasError = false;
        records().forEach((r) => {
          if (r.record.error()) {
            errs[r.id] = r.record.error();
            hasError = true;
          }
        });
        return hasError ? errs : null;
      });

      const pricing = computed(() =>
        records()
          .map((r) => r.record()?.pricing)
          .filter((e) => !!e),
      );

      const prices = computed(() => {
        return pricing().map((p, index) => {
          const item = store.cartItems()[index];
          return {
            itemId: item.id,
            pricing: ItemPriceUtil.calculatePrice(item.quantity, p),
          };
        });
      });

      const subTotal = computed(() => {
        return ItemPriceUtil.fromIntegerPrice(
          prices().reduce((total, p) => total + ItemPriceUtil.toIntegerPrice(p?.pricing.price || 0), 0),
        );
      });
      const subTotalFees = computed(() =>
        ItemPriceUtil.fromIntegerPrice(prices().reduce((total, p) => total + ItemPriceUtil.toIntegerPrice(p?.pricing.feesTotal || 0), 0)),
      );
      const subTotalWithFees = computed(() =>
        ItemPriceUtil.fromIntegerPrice(
          prices().reduce((total, p) => total + ItemPriceUtil.toIntegerPrice(p?.pricing.priceWithFees || 0), 0),
        ),
      );

      return {
        cartPrices: prices,
        cartPricesLoading: loading,
        cartPricesErrors: errors,
        cartSubTotal: subTotal,
        cartSubTotalFees: subTotalFees,
        cartSubTotalWithFees: subTotalWithFees,
      };
    }),
    withHooks((store) => {
      const pricingStore = inject(ItemPricingStore);
      return {
        onInit: () => {
          effect(() => {
            const pricing: Record<string, ItemPricingEntity | undefined> = {};
            store
              .cartItems()
              .map((i) => {
                const id = stringifyItemId(i.id);
                const record = pricingStore.getItemPricing(i.id);
                return {
                  id,
                  record: record(),
                };
              })
              .forEach((p) => {
                pricing[p.id] = p.record;
              });
            patchState(store, { _pricing: pricing });
          });
        },
      };
    }),
  );
};
